import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import i18n from 'i18next';
import _ from 'lodash';

import { FETCH_DASHBOARD_ALERTS, FETCH_DASHBOARD_METRICS } from '../constants/ActionTypes';

import { showError } from '../actions/Common';
import { CustomAction } from 'model/state';
import { LocationMap } from 'model';
import {
  UserStatsResponse, responseToMetrics, AlertResponse, AlertsResponse, toAlert, toLocationFromPendingAlert
} from 'model/api';
import {
  DashboardPayload, fetchDashboardAlertsError, fetchDashboardAlertsSuccess, fetchDashboardMetricsError,
  fetchDashboardMetricsSuccess, updateDashboardAlerts
} from 'actions';
import { getUserStats } from 'api/User';
import { getAlerts } from "../api/Alerts";

function* fetchMetricsWorker(action: CustomAction<DashboardPayload>) {
  const userId = action.payload?.userId;
  try {
    if (!userId) {
      throw Error('Invalid Parameters');
    }
    const userStats: UserStatsResponse = yield call(getUserStats, userId);
    yield put(fetchDashboardMetricsSuccess(responseToMetrics(userStats)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchDashboardMetricsError());
  }
}

function* fetchDashboardAlertsWorker(action: CustomAction<DashboardPayload>) {
  const userId = action.payload?.userId;
  try {
    if (!userId) {
      throw Error('Invalid Parameters');
    }

    let locationDict: LocationMap = {};
    let page = 1;
    const size = 50;
    let moreAlerts = true;
    let alertCount = 0;
    while (moreAlerts) {
      const alerts: AlertsResponse = yield call(getAlerts, undefined, userId, ['critical', 'warning'], 'triggered', undefined, page, size);
      locationDict = alerts.items.reduce((acc, alert) => processAlert(acc, alert), locationDict);

      const locations = _.map(locationDict, loc => loc);
      yield put(updateDashboardAlerts(locations));

      alertCount += alerts.items.length;
      moreAlerts = alertCount < alerts.total;
    }

    yield put(fetchDashboardAlertsSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchDashboardAlertsError());
  }
}

function processAlert(locationMap: LocationMap, alertResponse: AlertResponse): LocationMap {
  if (locationMap[alertResponse.locationId]) {
    // Location already exists

    const stats = locationMap[alertResponse.locationId].notifications?.pending || { infoCount: 0, warningCount: 0, criticalCount: 0 };
    if (alertResponse.alarm.severity === 'critical') {
      stats.criticalCount++;
    } else if (alertResponse.alarm.severity === 'warning') {
      stats.warningCount++;
    }

    return {
      ...locationMap,
      [alertResponse.locationId]: {
        ...locationMap[alertResponse.locationId],
        alerts: [
          ...(locationMap[alertResponse.locationId].alerts || []),
          toAlert(alertResponse)
        ],
        notifications: {
          pending: { ...stats }
        }
      }
    }
  } else {
    // New location
    return {
      ...locationMap,
      [alertResponse.locationId]: toLocationFromPendingAlert(alertResponse),
    }
  }
}

export function* fetchMetricsSaga() {
  yield takeLatest(FETCH_DASHBOARD_METRICS, fetchMetricsWorker);
}

export function* fetchDashboardAlertsSaga() {
  yield takeLatest(FETCH_DASHBOARD_ALERTS, fetchDashboardAlertsWorker)
}

export default function* dashboardSaga() {
  yield all([
    fork(fetchMetricsSaga),
    fork(fetchDashboardAlertsSaga),
  ]);
}
